import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, Clock } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount).replace('MYR', 'RM');
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-MY', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
};

const getInvestorTypeColor = (type) => {
  switch (type) {
    case 'Angel Investor':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'Individual HNWI':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'Family Office':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'Venture Capital / Private Equity':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'Strategic / Corporate Investor':
      return 'bg-rose-100 text-rose-700 border-rose-200';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const LoadingSkeleton = () => (
  <div className="space-y-3">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex items-center gap-4 py-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-6 w-24" />
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className="text-center py-12">
    <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted mb-4">
      <Users className="h-8 w-8 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-medium text-foreground mb-2">No Pledges Yet</h3>
    <p className="text-muted-foreground text-sm">
      Be the first to submit an investment pledge!
    </p>
  </div>
);

export const PledgeActivity = ({ pledges, loading }) => {
  return (
    <Card className="border shadow-lg h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-display flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
              </span>
              Live Pledge Activity
            </CardTitle>
            <CardDescription>
              Recent investment pledges from our investor community
            </CardDescription>
          </div>
          {pledges.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {pledges.length} pledge{pledges.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {loading ? (
          <LoadingSkeleton />
        ) : pledges.length === 0 ? (
          <EmptyState />
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[180px]">Investor</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right w-[100px]">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pledges.map((pledge, index) => (
                  <TableRow 
                    key={pledge.id} 
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                          {pledge.display_name.charAt(0).toUpperCase()}
                        </div>
                        <span className="truncate max-w-[120px]">{pledge.display_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${getInvestorTypeColor(pledge.investor_type)}`}>
                        {pledge.investor_type.split(' / ')[0]}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-foreground">
                      {formatCurrency(pledge.pledge_amount)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(pledge.timestamp)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default PledgeActivity;
